import React, { useEffect, useState, useMemo } from "react";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { RichTextEditor } from "../../../components/RichTextEditor";
import { InputText } from "../../../components/InputText";
import { apostilasService, Apostila } from "../../../services/apostilasService";
import {
  categoriasApostilasService,
  CategoriaApostila,
} from "../../../services/categoriasApostilasService";
import { api } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  ArrowUp,
  ArrowDown,
  X,
  Save,
  Eye,
  EyeOff,
  GripVertical,
  FolderOpen,
  Lock,
  Shield,
  ChevronDown,
} from "lucide-react";

interface PatenteOption {
  id: string;
  patente: string;
  abreviacao?: string;
  categoria?: string;
  ordem: number;
}

interface PatentesMultiSelectProps {
  label: string;
  patentes: PatenteOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  hint?: string;
}

function PatentesMultiSelect({ label, patentes, selected, onChange, hint }: PatentesMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const selectedNames = patentes
    .filter(p => selected.includes(p.id))
    .map(p => p.abreviacao || p.patente);

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  }

  // Agrupar por categoria
  const grupos = useMemo(() => {
    const map = new Map<string, PatenteOption[]>();
    for (const p of patentes) {
      const cat = p.categoria || "Outras";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(p);
    }
    return map;
  }, [patentes]);

  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {hint && <p className="text-xs text-muted-foreground/70">{hint}</p>}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <span className={selected.length === 0 ? "text-muted-foreground" : "text-foreground"}>
            {selected.length === 0
              ? "Nenhuma selecionada"
              : selectedNames.join(", ")}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background shadow-lg max-h-56 overflow-y-auto">
            {Array.from(grupos.entries()).map(([cat, pats]) => (
              <div key={cat}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                  {cat}
                </div>
                {pats.map(p => (
                  <label
                    key={p.id}
                    className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-accent/50 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(p.id)}
                      onChange={() => toggle(p.id)}
                      className="h-4 w-4 rounded border-gray-300 accent-primary"
                    />
                    <span>{p.patente}</span>
                    {p.abreviacao && (
                      <span className="text-xs text-muted-foreground">({p.abreviacao})</span>
                    )}
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {patentes.filter(p => selected.includes(p.id)).map(p => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
            >
              {p.abreviacao || p.patente}
              <button
                type="button"
                onClick={() => toggle(p.id)}
                className="ml-0.5 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function Apostilas() {
  const navigate = useNavigate();
  const { militar } = useAuth();
  const [apostilas, setApostilas] = useState<Apostila[]>([]);
  const [categorias, setCategorias] = useState<CategoriaApostila[]>([]);
  const [patentesDisponiveis, setPatentesDisponiveis] = useState<PatenteOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal de criação/edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Apostila | null>(null);
  const [formTitulo, setFormTitulo] = useState("");
  const [formDescricao, setFormDescricao] = useState("");
  const [formConteudo, setFormConteudo] = useState("");
  const [formPublicado, setFormPublicado] = useState(false);
  const [formCategoriaId, setFormCategoriaId] = useState<string>("");
  const [formPatentesVisualizar, setFormPatentesVisualizar] = useState<string[]>([]);
  const [formPatentesEditar, setFormPatentesEditar] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Confirmação de exclusão
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadApostilas();
    loadCategorias();
    loadPatentes();
  }, []);

  async function loadPatentes() {
    try {
      const response = await api.get("/patentes");
      const data = Array.isArray(response.data) ? response.data : [];
      setPatentesDisponiveis(data.sort((a: PatenteOption, b: PatenteOption) => a.ordem - b.ordem));
    } catch {
      // silencioso
    }
  }

  async function loadApostilas() {
    try {
      setLoading(true);
      const data = await apostilasService.list();
      setApostilas(data);
    } catch {
      enqueueSnackbar("Erro ao carregar apostilas", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function loadCategorias() {
    try {
      const data = await categoriasApostilasService.list();
      setCategorias(data);
    } catch {
      // silencioso — categorias são opcionais
    }
  }

  function openCreate() {
    setEditing(null);
    setFormTitulo("");
    setFormDescricao("");
    setFormConteudo("");
    setFormPublicado(false);
    setFormCategoriaId("");
    setFormPatentesVisualizar([]);
    setFormPatentesEditar([]);
    setIsModalOpen(true);
  }

  function openEdit(apostila: Apostila) {
    setEditing(apostila);
    setFormTitulo(apostila.titulo);
    setFormDescricao(apostila.descricao || "");
    setFormConteudo(apostila.conteudo_html);
    setFormPublicado(apostila.publicado);
    setFormCategoriaId(apostila.categoria_id || "");
    const pats = apostila.apostilaPatentes || [];
    setFormPatentesVisualizar(pats.filter(p => !p.pode_editar).map(p => p.patente_id));
    setFormPatentesEditar(pats.filter(p => p.pode_editar).map(p => p.patente_id));
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditing(null);
  }

  async function handleSave() {
    if (!formTitulo.trim()) {
      enqueueSnackbar("O título é obrigatório", { variant: "warning" });
      return;
    }

    try {
      setSaving(true);
      if (editing) {
        await apostilasService.update(editing.id, {
          titulo: formTitulo,
          descricao: formDescricao || undefined,
          conteudo_html: formConteudo,
          publicado: formPublicado,
          categoria_id: formCategoriaId || null,
          patentes_visualizar: formPatentesVisualizar,
          patentes_editar: formPatentesEditar,
        });
        enqueueSnackbar("Apostila atualizada com sucesso!", { variant: "success" });
      } else {
        await apostilasService.create({
          titulo: formTitulo,
          descricao: formDescricao || undefined,
          conteudo_html: formConteudo,
          publicado: formPublicado,
          categoria_id: formCategoriaId || undefined,
          patentes_visualizar: formPatentesVisualizar,
          patentes_editar: formPatentesEditar,
        });
        enqueueSnackbar("Apostila criada com sucesso!", { variant: "success" });
      }
      closeModal();
      loadApostilas();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao salvar apostila",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  }

  function canEditApostila(apostila: Apostila): boolean {
    const editPatentes = (apostila.apostilaPatentes || []).filter(p => p.pode_editar);
    if (editPatentes.length === 0) return true; // sem restrição
    return editPatentes.some(p => String(p.patente_id) === String(militar?.id_patente));
  }

  async function handleDelete(id: string) {
    try {
      await apostilasService.delete(id);
      enqueueSnackbar("Apostila excluída com sucesso!", { variant: "success" });
      setDeletingId(null);
      loadApostilas();
    } catch {
      enqueueSnackbar("Erro ao excluir apostila", { variant: "error" });
    }
  }

  // Apostilas agrupadas por categoria, respeitando ordem das categorias e ordem interna
  const apostilasPorCategoria = useMemo(() => {
    const sorted = [...categorias].sort((a, b) => a.ordem - b.ordem);
    const grupos: Array<{ categoriaId: string | null; nome: string; apostilas: Apostila[] }> =
      sorted.map((cat) => ({
        categoriaId: cat.id,
        nome: cat.nome,
        apostilas: apostilas
          .filter((a) => a.categoria_id === cat.id)
          .sort((a, b) => a.ordem - b.ordem),
      }));

    const semCategoria = apostilas
      .filter((a) => !a.categoria_id)
      .sort((a, b) => a.ordem - b.ordem);
    if (semCategoria.length > 0) {
      grupos.push({ categoriaId: null, nome: "Sem categoria", apostilas: semCategoria });
    }

    return grupos.filter((g) => g.apostilas.length > 0);
  }, [apostilas, categorias]);

  async function moveUpInGroup(apostila: Apostila, grupo: Apostila[]) {
    const idx = grupo.findIndex((a) => a.id === apostila.id);
    if (idx <= 0) return;
    const anterior = grupo[idx - 1];
    const ordens = [
      { id: apostila.id, ordem: anterior.ordem },
      { id: anterior.id, ordem: apostila.ordem },
    ];
    try {
      await apostilasService.reordenar({ ordens });
      setApostilas((prev) =>
        prev.map((a) => {
          const found = ordens.find((o) => o.id === a.id);
          return found ? { ...a, ordem: found.ordem } : a;
        })
      );
    } catch {
      enqueueSnackbar("Erro ao reordenar apostilas", { variant: "error" });
    }
  }

  async function moveDownInGroup(apostila: Apostila, grupo: Apostila[]) {
    const idx = grupo.findIndex((a) => a.id === apostila.id);
    if (idx >= grupo.length - 1) return;
    const proximo = grupo[idx + 1];
    const ordens = [
      { id: apostila.id, ordem: proximo.ordem },
      { id: proximo.id, ordem: apostila.ordem },
    ];
    try {
      await apostilasService.reordenar({ ordens });
      setApostilas((prev) =>
        prev.map((a) => {
          const found = ordens.find((o) => o.id === a.id);
          return found ? { ...a, ordem: found.ordem } : a;
        })
      );
    } catch {
      enqueueSnackbar("Erro ao reordenar apostilas", { variant: "error" });
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <BookOpen className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Apostilas</h1>
            <p className="text-sm text-muted-foreground">
              Gerencie as apostilas exibidas no site
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/informacoes/apostilas/categorias")}
            className="flex items-center gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            Categorias
          </Button>
          <Button onClick={openCreate} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Nova Apostila
          </Button>
        </div>
      </div>

      {/* Lista */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            Apostilas cadastradas ({apostilas.length})
            {categorias.length > 0 && (
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                em {apostilasPorCategoria.length} categoria{apostilasPorCategoria.length !== 1 ? "s" : ""}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : apostilas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
              <BookOpen className="h-10 w-10 opacity-30" />
              <p>Nenhuma apostila cadastrada.</p>
              <Button variant="outline" size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Criar primeira apostila
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {apostilasPorCategoria.map((grupo) => (
                <div key={grupo.categoriaId ?? "sem-categoria"}>
                  {/* Separador de categoria */}
                  <div className="flex items-center gap-2 mb-3">
                    <FolderOpen className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-semibold text-foreground">{grupo.nome}</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {grupo.apostilas.length}
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* Apostilas do grupo */}
                  <div className="space-y-2 pl-2 border-l-2 border-primary/10">
                    {grupo.apostilas.map((apostila, index) => (
                      <div
                        key={apostila.id}
                        className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:bg-accent/30 transition-colors"
                      >
                        {/* Ordem */}
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                          {apostila.ordem}
                        </span>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground truncate">{apostila.titulo}</p>
                          {apostila.descricao && (
                            <p className="text-sm text-muted-foreground truncate">{apostila.descricao}</p>
                          )}
                        </div>

                        {/* Status publicado */}
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            apostila.publicado
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {apostila.publicado ? (
                            <><Eye className="h-3 w-3" />Publicada</>
                          ) : (
                            <><EyeOff className="h-3 w-3" />Rascunho</>
                          )}
                        </span>

                        {/* Reordenação dentro do grupo */}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveUpInGroup(apostila, grupo.apostilas)}
                            disabled={index === 0}
                            className="h-8 w-8 p-0"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveDownInGroup(apostila, grupo.apostilas)}
                            disabled={index === grupo.apostilas.length - 1}
                            className="h-8 w-8 p-0"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Editar */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(apostila)}
                          disabled={!canEditApostila(apostila)}
                          title={!canEditApostila(apostila) ? "Sua patente não tem permissão para editar esta apostila" : "Editar"}
                          className="h-8 w-8 p-0"
                        >
                          {canEditApostila(apostila) ? <Edit className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        </Button>

                        {/* Excluir */}
                        {deletingId === apostila.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-destructive font-medium">Confirmar?</span>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(apostila.id)}
                              className="h-7 px-2 text-xs"
                            >
                              Sim
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeletingId(null)}
                              className="h-7 px-2 text-xs"
                            >
                              Não
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingId(apostila.id)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de criação/edição */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto py-8 px-4">
          <div className="w-full max-w-3xl rounded-xl border border-border bg-background shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="text-lg font-semibold text-foreground">
                {editing ? "Editar Apostila" : "Nova Apostila"}
              </h2>
              <Button variant="ghost" size="sm" onClick={closeModal} className="h-8 w-8 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Título */}
              <div className="space-y-2">
                <Label htmlFor="titulo">Título *</Label>
                <InputText
                  id="titulo"
                  placeholder="Nome da apostila"
                  value={formTitulo}
                  onChange={(e) => setFormTitulo(e.target.value)}
                />
              </div>

              {/* Descrição */}
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição (opcional)</Label>
                <InputText
                  id="descricao"
                  placeholder="Breve descrição sobre o conteúdo"
                  value={formDescricao}
                  onChange={(e) => setFormDescricao(e.target.value)}
                />
              </div>

              {/* Categoria */}
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria (opcional)</Label>
                <select
                  id="categoria"
                  value={formCategoriaId}
                  onChange={(e) => setFormCategoriaId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">— Sem categoria —</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Publicado */}
              <div className="flex items-center gap-3">
                <Switch
                  id="publicado"
                  checked={formPublicado}
                  onCheckedChange={setFormPublicado}
                />
                <Label htmlFor="publicado" className="flex items-center gap-2 cursor-pointer">
                  {formPublicado ? (
                    <><Eye className="h-4 w-4 text-green-600" /><span className="text-green-600">Publicada no site</span></>
                  ) : (
                    <><EyeOff className="h-4 w-4 text-muted-foreground" /><span className="text-muted-foreground">Rascunho (não visível)</span></>
                  )}
                </Label>
              </div>

              {/* Conteúdo HTML */}
              <div className="space-y-2">
                <Label>Conteúdo</Label>
                <div className="rounded-md border border-border overflow-hidden">
                  <RichTextEditor
                    value={formConteudo}
                    onChange={setFormConteudo}
                    enableImages
                    height="400px"
                  />
                </div>
              </div>

              {/* Permissões por patente */}
              <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">Permissões por Patente</span>
                  <span className="text-xs text-muted-foreground">(deixe vazio para todos)</span>
                </div>

                {/* Patentes que podem visualizar */}
                <PatentesMultiSelect
                  label="Patentes que podem visualizar"
                  patentes={patentesDisponiveis}
                  selected={formPatentesVisualizar}
                  onChange={setFormPatentesVisualizar}
                  hint="Sem seleção = visível para todos no painel"
                />

                {/* Patentes que podem editar */}
                <PatentesMultiSelect
                  label="Patentes que podem editar"
                  patentes={patentesDisponiveis}
                  selected={formPatentesEditar}
                  onChange={setFormPatentesEditar}
                  hint="Sem seleção = qualquer um com permissão de perfil pode editar"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
